import React from "react";

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="container mx-auto py-16 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6 text-primary">KeepMore Privacy Policy Statement</h1>
      <p className="text-sm text-gray-500 mb-8">Effective Date: July 30, 2025</p>
      <p className="mb-6">This Privacy Policy explains how KeepMore, Inc. ("KeepMore", "we", "us", or "our") collects, uses, and shares information about you when you use our services, including our web application and client-facing checkout pages.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">Information We Collect</h2>
      <p className="mb-4">
        We only collect information when we have a reason to do so—for example, to provide the service, secure your account, communicate with you, or improve the platform.
      </p>
      <ul className="list-disc list-inside space-y-4 mb-6">
        <li>
          <strong>Account Information:</strong> When you create an account, we collect your name, email, and encrypted password. We also collect any business information you provide for your public-facing checkout pages.
        </li>
        <li>
          <strong>Payment Information:</strong> We use Stripe for all payment processing. When you connect a Stripe account, we receive data from Stripe, such as your account ID and transaction data. All customer payments go directly through Stripe.
        </li>
        <li>
          <strong>Log & Usage Data:</strong> We collect basic usage data, including IP address, browser type, device info, and activity logs within KeepMore. This helps us secure the service and understand user behavior.
        </li>
        <li>
          <strong>Tracking Technologies:</strong> We may use cookies, pixels, and similar technologies to measure usage, track page views, run experiments, and improve the user experience.
        </li>
        <li>
          <strong>Support Communications:</strong> If you contact us for support or feedback, we retain those messages to help resolve issues and improve our service.
        </li>
        <li>
          <strong>Checkout Pages:</strong> KeepMore generates hosted checkout pages for your customers. These pages are public (anyone with the link can view them), but they are not indexed by search engines.
        </li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-2">How We Use Your Information</h2>
      <ul className="list-disc list-inside space-y-2 mb-6">
        <li>Provide and maintain our services</li>
        <li>Communicate with you</li>
        <li>Analyze platform usage</li>
        <li>Improve our features and user experience</li>
        <li>Secure our platform</li>
        <li>Comply with applicable legal requirements</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-2">Sharing Your Information</h2>
      <ul className="list-disc list-inside space-y-2 mb-6">
        <p className="mb-4">We do not sell your personal data. We may share limited information under the following circumstances:</p>
        <li>With service providers (e.g., Stripe for payments)</li>
        <li>If required by law, such as a subpoena or court order</li>
        <li>To protect rights and safety, including investigating fraud or abuse</li>
        <li>With contractors or vendors who help us operate the service under confidentiality agreements</li>
        <li>If the business is acquired or merged, your data may be transferred, but this policy would still apply</li>
        <li>With your consent, or when you intentionally share data on a public-facing checkout page</li>
        <li>In aggregated or de-identified form, for analytics or research</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-2">Data Retention</h2>
      <p className="mb-6">We keep your data for as long as needed to provide the service and fulfill legal obligations. Transactional and audit data related to Stripe may be retained indefinitely for compliance and record-keeping purposes.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">Security</h2>
      <p className="mb-6">We take reasonable precautions to protect your data, but no system is completely secure. Use strong passwords and protect your login credentials.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">Choices</h2>
      <p className="mb-6">You can update or delete your account data by logging into your dashboard. You can also contact us if you have a request or concern.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">Contact</h2>
      <p className="mb-6">If you have questions about this Privacy Policy or your data, contact us at: <a href="mailto:support@keepmore.com" className="text-blue-600 underline">support@keepmore.com</a></p>
    </div>
  );
};

export default PrivacyPolicy;
